import { Helmet } from 'react-helmet';

import { MaoriLanguage } from '@/components/Accessibility/MaoriLanguage';
import { LegalBody2, LegalH2, LegalPageWrapper, LegalUl } from '@/components/LegalPages/LegalPages';
import { H2 } from '@/components/Typography/Typography';
import { Fineprint } from '@/components/Typography/Typography';

const TermsOfUsePage = () => {
  return (
    <>
      <Helmet>
        <title>Tātai | Terms of use</title>
        <meta
          name="description"
          content="This document outlines your rights and responsibilities when using the Tātai service, whether you access it anonymously or through My Health Account. Tātai enables individuals to record and manage iwi affiliation information securely and respectfully."
        />
        <meta
          property="og:description"
          content="This document outlines your rights and responsibilities when using the Tātai service, whether you access it anonymously or through My Health Account. Tātai enables individuals to record and manage iwi affiliation information securely and respectfully."
        />
      </Helmet>
      <LegalPageWrapper>
        <H2 as="h1">
          <MaoriLanguage>Tātai</MaoriLanguage> - Terms of Use
        </H2>

        <LegalH2>About these terms</LegalH2>
        <LegalBody2>
          Welcome to the <MaoriLanguage>Tātai</MaoriLanguage> Terms of Use. This document outlines your rights and
          responsibilities when using the <MaoriLanguage>Tātai</MaoriLanguage> service, whether you access it
          anonymously or through My Health Account.
        </LegalBody2>
        <LegalBody2>
          <MaoriLanguage>Tātai</MaoriLanguage> enables individuals to record and manage iwi affiliation information
          securely and respectfully.
        </LegalBody2>
        <LegalBody2>
          By using this website, whether anonymously without logging in or by logging in through My Health Account, you
          agree to these terms of use.
        </LegalBody2>

        <LegalH2>Using the service</LegalH2>
        <LegalBody2>You may:</LegalBody2>
        <LegalUl>
          <li>
            use the service without logging in to My Health Account to record iwi information. You will not be able to
            edit it later unless you log in with My Health Account
          </li>
          <li>log in with My Health Account to view, update, or delete your information at any time</li>
          <li>
            complete iwi affiliation for another person, but only with their informed consent or if you are their legal
            guardian.
          </li>
        </LegalUl>
        <LegalBody2>
          If you do not log in to My Health Account, the submission will not be linked to your account.
        </LegalBody2>

        <LegalH2>Your responsibilities</LegalH2>
        <LegalBody2>By using this service, you agree to:</LegalBody2>
        <LegalUl>
          <li>provide accurate and truthful information</li>
          <li>use the site respectfully and lawfully</li>
          <li>not misuse, interfere with, or compromise the system or others&apos; information</li>
          <li>be responsible for any actions taken under your login.</li>
        </LegalUl>

        <LegalH2>Submitting information</LegalH2>
        <LegalBody2>
          Once your iwi affiliation is submitted, it will go through brief automated system checks to correctly link and
          save your information.
        </LegalBody2>
        <LegalBody2>
          During this time, editing your information may be temporarily disabled to ensure your submission is correctly
          recorded.
        </LegalBody2>

        <LegalH2>Acting on behalf of others</LegalH2>
        <LegalBody2>If you are completing the form on behalf of someone else, you must:</LegalBody2>
        <LegalUl>
          <li>have their informed consent, or</li>
          <li>be their legal guardian or authorised representative.</li>
        </LegalUl>
        <LegalBody2>
          You are responsible for the accuracy, truthfulness, and appropriateness of any information submitted on behalf
          of another person.
        </LegalBody2>
        <LegalBody2>
          If the person later links their submission to their own My Health Account, full control of the information
          transfers to them.
        </LegalBody2>
        <LegalBody2>
          From that point, they will manage their own iwi affiliation record, and you will no longer be able to view,
          edit, or delete it.
        </LegalBody2>

        <LegalH2>Intellectual property</LegalH2>
        <LegalBody2>
          Unless otherwise stated, all content, materials, and design elements on this website are owned by{' '}
          <MaoriLanguage>Manatū Hauora</MaoriLanguage> - Ministry of Health and Te Whatu Ora - Health NZ.
        </LegalBody2>
        <LegalBody2>
          You may not copy, republish, distribute, or reuse any part of this service without prior written permission.
        </LegalBody2>
        <LegalBody2>
          You retain ownership of the iwi affiliation information you submit through this service.
        </LegalBody2>

        <LegalH2>Disclaimers and limitations of liability</LegalH2>
        <LegalBody2>We aim to ensure this service is accurate, secure, and available, but:</LegalBody2>
        <LegalUl>
          <li>we do not guarantee uninterrupted access or error-free performance</li>
          <li>we are not liable for any loss resulting from your use of the service, unless required by law</li>
          <li>you are responsible for the accuracy of the information you submit.</li>
        </LegalUl>
        <LegalBody2>This does not limit any rights you have under Aotearoa New Zealand law.</LegalBody2>

        <LegalH2>Changes to these terms</LegalH2>
        <LegalBody2>These terms may be updated from time to time. If so:</LegalBody2>
        <LegalUl>
          <li>we will post the new version here</li>
          <li>the &ldquo;last updated&rdquo; date will change</li>
          <li>your continued use of the service means you accept the updated terms.</li>
        </LegalUl>

        <LegalH2>Governing law</LegalH2>
        <LegalBody2>These terms are governed by the laws of Aotearoa New Zealand.</LegalBody2>

        <LegalH2>Privacy and your information</LegalH2>
        <LegalBody2>To understand how your information is managed, please read the:</LegalBody2>
        <LegalUl>
          <li>
            <a
              href="https://www.tewhatuora.govt.nz/health-services-and-programmes/digital-health/my-health-account/privacy-statement"
              target="_blank"
              rel="noreferrer noopener"
            >
              My Health Account Privacy Statement
            </a>
          </li>
          <li>
            <a href="https://tatai.maori.nz/privacy-policy" target="_blank" rel="noreferrer noopener">
              <MaoriLanguage>Tātai</MaoriLanguage> Privacy Statement
            </a>
          </li>
        </LegalUl>

        <LegalH2>Contact</LegalH2>
        <LegalBody2>
          Please contact us at <a href="mailto:contact@tatai.maori.nz">contact@tatai.maori.nz</a> for:
        </LegalBody2>
        <LegalUl>
          <li>questions, support, or privacy concerns</li>
          <li>requests to access, correct, or delete information submitted without logging in to My Health Account.</li>
        </LegalUl>
        <LegalBody2>
          If you are not satisfied with our response, you may contact the{' '}
          <a href="https://privacy.org.nz/about-us/contact-us" target="_blank" rel="noreferrer noopener">
            Office of the Privacy Commissioner
          </a>
          .
        </LegalBody2>

        <Fineprint>Last updated: 9 April 2026</Fineprint>
      </LegalPageWrapper>
    </>
  );
};

export default TermsOfUsePage;
